import InventoryFileCategory from './enums/InventoryFileCategory';
import InventoryFileCategoryTitle from './enums/InventoryFileCategoryTitle';

/**
 * Вложения оборудования: правила отбора, разбора и показа.
 *
 * Здесь всё, что можно проверить без браузера, — и именно поэтому оно здесь, а не
 * в компоненте: компонент вкладки в тестах не монтируется (так же устроены inventoryCard.js
 * и inventoryPreview.js). Компонент отвечает за разметку и запросы, этот модуль — за
 * решения: пройдёт ли файл, фотография это или документ, как назвать размер.
 *
 * ПРОВЕРКА ЗДЕСЬ — НЕ ЗАЩИТА, А ВЕЖЛИВОСТЬ. Источник правды о допустимом файле —
 * App\Services\Inventory\InventoryItemFileService: он отклонит то же самое и тогда, когда
 * запрос придёт мимо интерфейса. Смысл проверки на клиенте один: не гнать 20 МБ по
 * мобильной сети ради отказа на той стороне.
 *
 * ПОТОЛКИ ПРИХОДЯТ С БЭКЕНДА вместе со списком вложений (limits в ответе). Значения ниже —
 * запасные, на случай ответа неожиданной формы; расходиться они могут только в сторону
 * более строгой проверки на клиенте.
 *
 * СОБРАННОГО АДРЕСА ФАЙЛА ЗДЕСЬ НЕТ, и это намеренно. Вложения оборудования не имеют
 * открытой ссылки вовсе: содержимое отдаёт только GET /inventory/items/{id}/files/{id}/download
 * под inventory:view с городской областью, то есть с заголовком авторизации. <img src>
 * такой заголовок не отправляет, поэтому кадр забирается запросом и показывается через
 * object URL — см. VInventoryItemFiles.
 */

/**
 * Потолок размера одного вложения, байты.
 *
 * 20 МиБ — не выбор интерфейса, а предел инфраструктуры: post_max_size и nginx
 * client_max_body_size стоят на 20M, и запрос крупнее до приложения не доедет вовсе.
 */
export const MAX_FILE_SIZE = 20971520;

/**
 * Потолок длины подписи к вложению. Совпадает с колонкой comment.
 */
export const MAX_COMMENT_LENGTH = 500;

/**
 * Разрешённые mime-типы. Зеркало InventoryItemFileService::ALLOWED_MIME_TYPES.
 */
export const ALLOWED_MIME_TYPES = [
    'image/jpeg',
    'image/pjpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/heic',
    'image/heif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/rtf',
    'text/plain',
    'text/csv',
];

/**
 * Разрешённые расширения. Зеркало InventoryItemFileService::ALLOWED_EXTENSIONS.
 */
export const ALLOWED_EXTENSIONS = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'bmp',
    'heic',
    'heif',
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'rtf',
    'txt',
    'csv',
];

/**
 * Разрешённые mime-типы ТОЛЬКО для изображений. Зеркало
 * InventoryPresetPreviewService::ALLOWED_MIME_TYPES.
 *
 * Считается из общего списка, а не выписывается вторым: две руками написанные копии
 * одного правила расходятся при первом же добавлении формата.
 */
export const IMAGE_MIME_TYPES = ALLOWED_MIME_TYPES.filter((type) => /^image\//.test(type));

/**
 * Разрешённые расширения изображений. Зеркало
 * InventoryPresetPreviewService::ALLOWED_EXTENSIONS.
 */
export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif'];

/**
 * Потолки для полей, куда принимается только фотография, — типового изображения модели
 * в первую очередь.
 *
 * Отдельной функцией, а не константой: validateInventoryFile() принимает объект потолков
 * и читает его поля, и отдавать один и тот же объект всем вызывающим значит однажды
 * получить его изменённым на месте.
 *
 * @returns {{maxFileSize: number, allowedMimeTypes: Array, allowedExtensions: Array}}
 */
export function photoOnlyLimits() {
    return {
        maxFileSize: MAX_FILE_SIZE,
        allowedMimeTypes: IMAGE_MIME_TYPES,
        allowedExtensions: IMAGE_EXTENSIONS,
    };
}

/**
 * Что принимает поле выбора файла на телефоне.
 *
 * Камера и галерея в одном списке: телефон сам предложит «Снять» или «Выбрать», а
 * accept="image/*" без расширений отсекал бы heic на части устройств, где браузер
 * не считает его картинкой.
 */
export const PHOTO_ACCEPT = 'image/*,.heic,.heif';

/**
 * Что принимает поле выбора документа.
 */
export const DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.rtf,.txt,.csv';

/**
 * Коды отказа. Отдельные значения, а не готовый текст: текст живёт рядом, в FILE_ERROR_TITLE,
 * и меняется без правки условий.
 */
export const FILE_ERROR_SIZE = 'size';
export const FILE_ERROR_TYPE = 'type';
export const FILE_ERROR_EMPTY = 'empty';
export const FILE_ERROR_COUNT = 'count';

/**
 * Тексты отказов.
 */
export const FILE_ERROR_TITLE = {
    [FILE_ERROR_SIZE]: 'Файл больше 20 МБ',
    [FILE_ERROR_TYPE]: 'Такой тип файла прикрепить нельзя',
    [FILE_ERROR_EMPTY]: 'Файл пустой',
    [FILE_ERROR_COUNT]: 'Больше файлов к одному предмету прикрепить нельзя',
};

/**
 * Категории для выпадающего списка, в порядке жизненного цикла предмета.
 *
 * Порядок не алфавитный намеренно: список читают глазами сверху вниз, и путь «приёмка →
 * выдача → возврат → ремонт → списание» — это то, как человек помнит историю техники.
 * «Общее» стоит первым как значение по умолчанию, «Прочее» последним как остаток.
 *
 * @returns {Array<{value: string, label: string}>}
 */
export function fileCategoryOptions() {
    return [
        InventoryFileCategory.GENERAL,
        InventoryFileCategory.WAREHOUSE_RECEIVE,
        InventoryFileCategory.ISSUE_TO_EMPLOYEE,
        InventoryFileCategory.RETURN_TO_WAREHOUSE,
        InventoryFileCategory.REPAIR_SEND,
        InventoryFileCategory.REPAIR_RETURN,
        InventoryFileCategory.WRITEOFF,
        InventoryFileCategory.OTHER,
    ].map((value) => ({ value, label: fileCategoryTitle(value) }));
}

/**
 * Название категории. Незнакомый код возвращается как есть — он заметен, а пустое место нет.
 *
 * @param {string} category
 * @returns {string}
 */
export function fileCategoryTitle(category) {
    return InventoryFileCategoryTitle[category] ?? String(category ?? '');
}

/**
 * Расширение имени файла в нижнем регистре, без точки.
 *
 * @param {string} name
 * @returns {string} Пустая строка, если расширения нет.
 */
export function fileExtension(name) {
    const parts = String(name ?? '').split('.');

    // Одна часть — расширения нет вовсе. «.gitignore» — тоже: у него нет имени, а не
    // расширение «gitignore».
    if (parts.length < 2 || parts[0] === '') {
        return '';
    }

    return parts[parts.length - 1].toLowerCase();
}

/**
 * Фотография ли это.
 *
 * По mime, а не по расширению: mime приходит и от браузера при выборе файла, и с бэкенда
 * в списке вложений, и это единственное поле, которое есть в обоих случаях.
 *
 * @param {{type: string}} file
 * @returns {boolean}
 */
export function isImageFile(file) {
    return /^image\//.test(String((file && file.type) ?? ''));
}

/**
 * Размер словами: килобайты до мегабайта, дальше мегабайты с одним знаком.
 *
 * Байты не показываются никогда: «184 320 Б» не говорит человеку ничего, кроме того,
 * что число большое.
 *
 * @param {number} size
 * @returns {string}
 */
export function formatFileSize(size) {
    const bytes = Number(size);

    if (!Number.isFinite(bytes) || bytes <= 0) {
        return '';
    }

    if (bytes < 1024) {
        return '1 КБ';
    }

    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} КБ`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} МБ`;
}

/**
 * Проверить выбранный файл до отправки.
 *
 * Возвращается КОД отказа, а не текст и не исключение: вызывающему нужно и показать
 * сообщение, и понять, о чём речь.
 *
 * @param {{name: string, type: string, size: number}} file Файл из input, не с бэкенда.
 * @param {{maxFileSize?: number, allowedMimeTypes?: Array, allowedExtensions?: Array}} limits
 *        Потолки с бэкенда; отсутствующие берутся из констант модуля.
 * @returns {string|null} Код отказа или null, если файл проходит.
 */
export function validateInventoryFile(file, limits = {}) {
    if (!file) {
        return FILE_ERROR_EMPTY;
    }

    const maxSize = Number(limits.maxFileSize) > 0 ? Number(limits.maxFileSize) : MAX_FILE_SIZE;
    const mimeTypes = Array.isArray(limits.allowedMimeTypes) ? limits.allowedMimeTypes : ALLOWED_MIME_TYPES;
    const extensions = Array.isArray(limits.allowedExtensions) ? limits.allowedExtensions : ALLOWED_EXTENSIONS;

    const size = Number(file.size);

    // Нулевой размер — это не «маленький файл», а сбой выбора: так выглядит папка,
    // перетащенная в поле, и файл, который камера не успела записать.
    if (!Number.isFinite(size) || size <= 0) {
        return FILE_ERROR_EMPTY;
    }

    if (size > maxSize) {
        return FILE_ERROR_SIZE;
    }

    // Обе проверки, как на бэкенде: mime называет браузер, и переименовать исполняемый
    // файл в «image/jpeg» ничего не стоит — расширение при этом останется своим.
    const mime = String(file.type ?? '')
        .split(';')[0]
        .trim()
        .toLowerCase();

    if (mimeTypes.indexOf(mime) === -1 || extensions.indexOf(fileExtension(file.name)) === -1) {
        return FILE_ERROR_TYPE;
    }

    return null;
}

/**
 * Текст отказа по коду.
 *
 * @param {string|null} error
 * @returns {string}
 */
export function fileErrorTitle(error) {
    return FILE_ERROR_TITLE[error] ?? '';
}

/**
 * Разложить вложения на фотографии и документы.
 *
 * Разделение не косметическое: фотографии смотрят плиткой и открывают по клику,
 * документы читают списком с именем и размером — это два разных способа показа,
 * и смешивать их в одну ленту значит делать хуже обоим.
 *
 * @param {Array} attachments Ответ GET /inventory/items/{id}/files.
 * @returns {{photos: Array, documents: Array}}
 */
export function splitInventoryFiles(attachments) {
    const list = Array.isArray(attachments) ? attachments : [];

    return {
        photos: list.filter((attachment) => isImageFile(attachment)),
        documents: list.filter((attachment) => !isImageFile(attachment)),
    };
}

/**
 * Можно ли сделать это вложение главным.
 *
 * Только фотография: признак существует ради картинки в шапке карточки, и документ там
 * показать нечем. Правило то же, что у InventoryItemFileService::guardPhoto() — по типу
 * содержимого, а не по категории вложения: категорию выбирает человек и может ошибиться.
 *
 * @param {{type: string}} attachment
 * @returns {boolean}
 */
export function canBePrimaryPhoto(attachment) {
    return isImageFile(attachment);
}

/**
 * Главное фото списка или null.
 *
 * Признак берётся у вложения, а не вычисляется «первым фото»: галерея не сортируется,
 * и порядок не значит ничего. Двух главных в ответе быть не может — это держит уникальный
 * индекс в базе, — но find() и при сбое вернёт одно, а не склеит два.
 *
 * @param {Array} attachments
 * @returns {Object|null}
 */
export function primaryPhotoOf(attachments) {
    const list = Array.isArray(attachments) ? attachments : [];

    return list.find((attachment) => attachment && attachment.is_primary && isImageFile(attachment)) ?? null;
}

/**
 * Список вложений после смены главного фото — БЕЗ повторного запроса.
 *
 * Зеркало того, что сделал сервер: у одного вложения признак встал, у всех остальных снят.
 * Перечитывать ради двух изменившихся признаков весь список значило бы заодно отпустить
 * и заново забрать содержимое всех кадров — на телефоне это мегабайты за нажатие.
 *
 * Чистая функция, а не правка на месте: новый массив нужен Vue, чтобы перерисовать плитку.
 *
 * @param {Array} attachments Текущий список.
 * @param {number|null} primaryId Номер вложения, ставшего главным; null — признак снят со всех.
 * @returns {Array}
 */
export function withPrimaryPhoto(attachments, primaryId) {
    const list = Array.isArray(attachments) ? attachments : [];

    return list.map((attachment) => {
        const isPrimary = primaryId !== null && attachment.id === primaryId;

        // Объект пересобирается только там, где признак действительно меняется: неизменные
        // вложения остаются теми же ссылками, и Vue не трогает их плитки.
        return Boolean(attachment.is_primary) === isPrimary ? attachment : { ...attachment, is_primary: isPrimary };
    });
}

/**
 * Потолки из ответа бэкенда в вид, который понимает validateInventoryFile().
 *
 * FAIL SAFE, а не fail closed: ответ неожиданной формы означает «проверяем по своим
 * константам», а не «ничего нельзя». Единственное, что решает клиентская проверка, —
 * показать отказ до отправки; запрет всего при потерянном ответе сломал бы загрузку
 * там, где сервер бы её принял.
 *
 * @param {*} raw Объект limits из ответа или что угодно вместо него.
 * @returns {{maxFileSize: number, maxFilesPerItem: (number|null), maxCommentLength: number,
 *            allowedMimeTypes: Array, allowedExtensions: Array}}
 */
export function normalizeFileLimits(raw) {
    const source = raw && typeof raw === 'object' ? raw : {};

    const number = (value, fallback) => (Number(value) > 0 ? Number(value) : fallback);
    const list = (value, fallback) => (Array.isArray(value) && value.length ? value : fallback);

    return {
        maxFileSize: number(source.max_file_size, MAX_FILE_SIZE),
        maxFilesPerItem: number(source.max_files_per_item, null),
        maxCommentLength: number(source.max_comment_length, MAX_COMMENT_LENGTH),
        allowedMimeTypes: list(source.allowed_mime_types, ALLOWED_MIME_TYPES),
        allowedExtensions: list(source.allowed_extensions, ALLOWED_EXTENSIONS),
    };
}
